<template>
  <app-landing-page-layout>
    <div class="flex h-screen overflow-y-auto">
      <div class="mx-auto mt-12">
        <div class="flex justify-center">
          <img class="w-24" src="/static/logos/parlay_logo.svg" alt="" />
        </div>
        <div v-show="show_captcha" class="recaptcha-container mt-4">
          <div id="g-recaptcha" :data-sitekey="recaptchaKey"></div>
          <div v-if="show_after_captcha_error">
            <button
              class="mt-4 px-4 py-2 bg-pinky hover:bg-pinky-dark rounded cursor-pointer text-white font-semibold"
              @click.prevent="$router.push({ name: 'login' })"
            >
              Back to login
            </button>
          </div>
        </div>
        <div
          v-show="!show_captcha"
          class="sign-up-box my-5 box-border shadow-sm p-8 border-1 rounded bg-white"
        >
          <div class="font-medium">
            <p class="mb-4 flex justify-center font-bold text-lg">
              Create an Account
            </p>
            <div class="flex justify-between mb-3">
              <button
                class="flex justify-center items-center w-full p-2 border border-gray-200 border-solid rounded mr-2 disabled:opacity-50"
                :class="{ 'bg-pink-100 border-pinky': user.role === 'teacher' }"
                @click="user.role = 'teacher'"
              >
                <img
                  v-if="user.role === 'teacher'"
                  src="/static/teacher_icons/teacher_sign_up_pink.svg"
                  class="h-8 w-8"
                />
                <img
                  v-else
                  src="/static/teacher_icons/teacher_sign_up_black.svg"
                  class="h-8 w-8"
                />
                <span
                  class="ml-2"
                  :class="{
                    'text-pinky font-semibold': user.role === 'teacher',
                  }"
                >
                  Teacher
                </span>
              </button>
              <button
                :disabled="school_invite_code"
                class="flex justify-center items-center w-full p-4 border border-gray-200 border-solid rounded ml-2 disabled:opacity-50"
                :class="{ 'bg-pink-100 border-pinky': user.role === 'student' }"
                @click="user.role = 'student'"
              >
                <i
                  :class="
                    user.role === 'student' ? 'text-pinky' : 'text-gray-700'
                  "
                  class="fas fa-graduation-cap text-2xl"
                ></i>
                <span
                  class="ml-2"
                  :class="{
                    'text-pinky font-semibold': user.role === 'student',
                  }"
                >
                  Student
                </span>
              </button>
            </div>
            <!-- User goes here to finish off SSO registration flow -->
            <div v-if="finalize_sso_registration && user.role !== ''">
              <div v-if="user.role === 'teacher'" class="my-6">
                Before you can complete your registration, you must accept the
                Parlay terms and conditions
              </div>
              <div v-else class="my-6">
                To complete your registration, you must insert the code your
                teacher gave you
              </div>
              <div v-if="user.role === 'teacher'" class="flex">
                <input v-model="has_agreed_to_terms" type="checkbox" />
                <span class="ml-2 text-xs">
                  I agree with
                  <a
                    href="https://parlayideas.com/policies/"
                    target="_blank"
                    class="text-pinky"
                  >
                    terms and conditions.</a
                  >
                </span>
              </div>
              <div v-else>
                <label class="font-semibold mt-1">Code</label>
                <input
                  required
                  v-model="user.invite_code"
                  class="bg-input text-sm text-gray-500 flex-grow outline-none px-4 py-2 w-full rounded"
                  type="text"
                />
              </div>
              <button
                :disabled="
                  (user.role === 'teacher' && !has_agreed_to_terms) ||
                  (user.role === 'student' && !user.invite_code)
                "
                class="mt-4 mb-6 px-4 py-2 w-full bg-pinky hover:bg-pinky-dark rounded text-white font-semibold disabled:opacity-50"
                @click="completeUserRegistration"
              >
                Submit
              </button>
            </div>
            <div v-else-if="user.role !== ''">
              <div>
                <google-button :signUp="true" :userRole="user.role" />
                <microsoft-button :signUp="true" :userRole="user.role" />
              </div>
              <div class="flex justify-center my-3 text-gray-400">
                <div class="line"><span>OR</span></div>
              </div>
              <div v-if="user.role === 'student'">
                <label class="font-semibold mt-1">Code</label>
                <input
                  required
                  @keyup="invite_code_error_message = null"
                  v-model="user.invite_code"
                  class="bg-input text-sm text-gray-500 flex-grow outline-none px-4 py-2 w-full rounded"
                  type="text"
                  :disabled="user.role === ''"
                />
                <p class="text-xs font-medium text-coolGray-500 mt-1">
                  Your teacher will give you a code to join the RoundTable
                </p>
                <div class="h-5 text-xs text-red-500">
                  {{ invite_code_error_message }}
                </div>
              </div>
              <div class="flex justify-between">
                <div class="mr-1">
                  <label class="font-semibold"
                    >First Name <span class="text-red-500">*</span></label
                  >
                  <input
                    required
                    @keyup="first_name_error_message = null"
                    v-model="first_name"
                    class="bg-input text-sm text-gray-500 flex-grow outline-none px-4 py-2 w-full rounded border-none"
                    style="border: none !important"
                    type="text"
                    :disabled="user.role === ''"
                  />
                  <div class="h-5 text-xs text-red-500">
                    {{ first_name_error_message }}
                  </div>
                </div>
                <div class="ml-1">
                  <label class="font-semibold"
                    >Last Name <span class="text-red-500">*</span></label
                  >
                  <input
                    required
                    @keyup="last_name_error_message = null"
                    v-model="last_name"
                    class="bg-input text-sm text-gray-500 flex-grow outline-none px-4 py-2 w-full rounded border-none"
                    style="border: none !important"
                    type="text"
                    :disabled="user.role === ''"
                  />
                  <div class="h-5 text-xs text-red-500">
                    {{ last_name_error_message }}
                  </div>
                </div>
              </div>
              <div>
                <label class="font-semibold"
                  >Email <span class="text-red-500">*</span></label
                >
                <input
                  required
                  @keyup="email_error_message = null"
                  v-model="user.email"
                  class="bg-input text-sm text-gray-500 flex-grow outline-none px-4 py-2 w-full rounded border-none"
                  style="border: none !important"
                  type="email"
                  :disabled="user.role === ''"
                />
                <div class="h-5 text-xs text-red-500">
                  {{ email_error_message }}
                </div>
              </div>
              <div>
                <label class="font-semibold mt-3"
                  >Password <span class="text-red-500">*</span></label
                >
                <password
                  v-model="user.password"
                  :badge="false"
                  :toggle="true"
                  @keyup.native="password_error_message = null"
                  :disabled="user.role === ''"
                />
                <div class="password-text text-gray-400 leading-3">
                  Use 8 ore more characters with a mix of letters, numbers &
                  symbols.
                </div>
                <div class="h-5 text-xs text-red-500">
                  {{ password_error_message }}
                </div>
              </div>
              <div>
                <label class="font-semibold"
                  >Confirm Password <span class="text-red-500">*</span></label
                >
                <input
                  required
                  @keyup.prevent="retype_password_error_message = null"
                  @keyup.enter="verifyUserInformation(user)"
                  v-model="user.retype_password"
                  class="bg-input text-sm text-gray-500 flex-grow outline-none px-4 py-2 w-full rounded border-none"
                  style="border: none !important"
                  type="password"
                  :disabled="user.role === ''"
                />
                <div class="mb-1 h-5 text-xs text-red-500">
                  {{ retype_password_error_message }}
                </div>
              </div>
              <div v-if="user.role === 'teacher'" class="flex mb-4">
                <input
                  v-model="has_agreed_to_terms"
                  type="checkbox"
                  :disabled="user.role === ''"
                />
                <span class="ml-2 text-xs">
                  I agree with
                  <a
                    href="https://parlayideas.com/policies/"
                    target="_blank"
                    class="text-pinky"
                  >
                    terms and conditions</a
                  >
                  and I am over 13 years old.
                </span>
              </div>
              <button
                :disabled="!has_agreed_to_terms && user.role === 'teacher'"
                class="mb-6 px-4 py-2 w-full bg-pinky hover:bg-pinky-dark rounded text-white font-semibold disabled:opacity-50"
                @click="verifyUserInformation(user)"
              >
                Submit
              </button>
            </div>
            
            <div class="flex justify-center text-xs">
              <span class="text-gray-400"> Already have an account?</span>
              <router-link :to="{ name: 'login' }" class="ml-2 text-pinky">
                Sign In
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </app-landing-page-layout>
</template>

<script>
  import googleButton from 'components/svgs/googleButton';
  import microsoftButton from 'components/svgs/microsoftButton';
  import authenticationServices from 'features/authentication/authentication.services';
  import store from 'store';
  import { EventBus } from 'utils/event-bus';
  import { environment } from 'services/apiConfigs';
  import Password from 'vue-password-strength-meter';

  export default {
    data() {
      return {
        user: {
          name: null,
          email: null,
          password: null,
          retype_password: null,
          role: '',
          school_id: this.$route.query.school_id || null,
          invite_code: null,
        },
        school_invite_code: null,
        first_name: null,
        last_name: null,
        has_agreed_to_terms: false,
        submitting_signup: false,
        user_has_submitted_code: false,
        retype_password_error_message: null,
        password_error_message: null,
        email_error_message: null,
        first_name_error_message: null,
        last_name_error_message: null,
        invite_code_error_message: null,
        show_registering_inputs: false,
        show_captcha: false,
        show_after_captcha_error: false,
        google_recaptcha_id: '',
        auth_res: null
      };
    },
    components: {
      appLandingPageLayout: require('layouts/appLandingPage/appLandingPage.vue'),
      chooseRole: require('components/chooseRole.vue'),
      teacherQuotes: require('features/authentication/components/teacherQuotes/teacherQuotes.vue'),
      landingPageTitle: require('features/authentication/components/landingPageTitle.vue'),
      googleButton,
      microsoftButton,
      Password,
    },
    computed: {
      registration_token() {
        return this.$route.query.tk;
      },
      show_role_selection() {
        return (
          !this.user.role &&
          (this.registration_token || this.show_registering_inputs)
        );
      },
      show_invite_code_input() {
        return this.user.role === 'student' && !this.user_has_submitted_code;
      },
      finalize_sso_registration() {
        // return this.registration_token && this.user.role === 'teacher';
        return this.registration_token;
      },
      recaptchaKey() {
        return process.env.GOOGLE_RECAPTCHA_KEY;
      },
      /* disabled_teacher_tab() {
        if (
          this.user.invite_code !== null &&
          this.user.invite_code !== '' &&
          this.user.invite_code &&
          this.user.invite_code.length
        )
          return true;
        return false;
      },*/
    },
    methods: {
      renderGoogleReCAPTCHA() {
        this.show_captcha = true;
        if (window.grecaptcha) {
          this.google_recaptcha_id = window.grecaptcha.render('g-recaptcha', {
            sitekey: this.recaptchaKey,
            callback: (verificationToken) => {
              this.user.verificationToken = verificationToken;
              this.completeUserRegistration();
            },
          });
        }
      },
      chooseRole(role) {
        this.user.role = role;
        if (role === 'teacher') {
          window.gtag('config', 'UA-90906114-1', {
            page_path: '/account/signup',
          });
        }
      },
      handleGoBack() {
        this.show_registering_inputs = false;
        this.user.role = null;
        this.user.invite_code = '';
        this.user_has_submitted_code = false;
      },
      verifyUserInformation(user) {
        if (
          user.role === 'student' &&
          (!user.invite_code || user.invite_code.trim() === '')
        ) {
          this.invite_code_error_message = 'Please enter your invite code';
          return;
        }
        if (!this.first_name || this.first_name.trim() === '') {
          this.first_name_error_message = 'Please enter your first name';
          return;
        }
        if (!this.last_name || this.last_name.trim() === '') {
          this.last_name_error_message = 'Please enter your last name';
          return;
        }
        if (!user.email || user.email.trim() === '') {
          this.email_error_message = 'Please enter your email';
          return;
        }
        if (!this.isAnEmail(user.email.trim())) {
          this.email_error_message = 'Invalid Email';
          return;
        }
        if (!user.password || user.password.length < 8) {
          this.password_error_message =
            'Please enter a password at least 8 characters in length';
          return;
        }
        if (user.password !== user.retype_password) {
          this.retype_password_error_message = 'Passwords do not match';
          return;
        }
        if (user.role === 'teacher' && !this.has_agreed_to_terms) {
          return;
        }
        user.name = `${this.first_name.trim()} ${this.last_name.trim()}`;
        user.email = this.user.email.toLowerCase();
        //captcha not working on staging-2
        environment === 'development' || environment === 'staging_two'
          ? this.completeUserRegistration()
          : this.renderGoogleReCAPTCHA();
      },
      completeUserRegistration() {
        localStorage.setItem(
          'course_sample',
          this.$store.state.course_sample_id
        );
        const payload = this.user;
        if (localStorage.getItem('register_invite_code')) {
          payload.school_invite_code = localStorage.getItem(
            'register_invite_code'
          );
          localStorage.removeItem('register_invite_code');
        }

        if (this.registration_token) {
          payload.registration_token = this.registration_token;
        }

        if (payload.invite_code) {
          if (payload.role === 'teacher') {
            payload.invite_code = null;
          } else {
            localStorage.setItem('invite_code', payload.invite_code);
          }
        }

        this.submitting_signup = true;
        authenticationServices.signup(payload).then((res) => {
          const { data } = res;
          this.auth_res = data;
          setTimeout(() => {
            store.dispatch('login', res.data.token);
            // the following line is here so we can save the school info to
            // local storage so we can display it in the updgrade success modal
            if (payload.school_invite_code) {
              if (res.data.school_info) {
                localStorage.setItem(
                  'schoolInfoPostUpgrade',
                  JSON.stringify(res.data.school_info)
                );
              } else if (res.data.show_email_suffixes_modal) {
                localStorage.setItem('showEmailSuffixesBlockModal', true);
                this.$store
                  .dispatch(
                    'getEmailSuffixesOfSchool',
                    payload.school_invite_code
                  )
                  .then((res) =>
                    EventBus.$emit('school_email_suffixes', res)
                  );
              } else {
                localStorage.setItem('showBlockUpgradeModal', true);
                this.$store
                  .dispatch(
                    'getAdminUsersOfSchool',
                    payload.school_invite_code
                  )
                  .then((res) => EventBus.$emit('school_admin_users', res));
              }
            }
          }, 300);
        })
        .catch((err) => {
          if (
            err.response.data.error.includes(
              'Please start registration process again'
            )
          ) {
            const toast = {
              type: 'Error',
              message:
                "Woops! Looks like something's not right. Please click the button to logout, and then sign back in.",
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          } else {
            EventBus.$emit('alert-msg', err.response.data.error);
          }
          if (document.getElementById('g-recaptcha')) {
            document.getElementById('g-recaptcha').remove();
          }
          if (localStorage.getItem('invite_code')) {
            localStorage.removeItem('invite_code', payload.invite_code);
          }
          this.submitting_signup = false;
          this.show_after_captcha_error = true;
        });
      },
      isAnEmail(email) {
        var regex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
      },
    },
    mounted() {
      const invite_code = localStorage.getItem('invite_code');
      if (invite_code) {
        this.user.invite_code = invite_code;
        this.user.role = 'student';
      }
      if (this.$route.query.school_invite_code) {
        localStorage.setItem(
          'register_invite_code',
          this.$route.query.school_invite_code
        );
        this.school_invite_code = this.$route.query.school_invite_code;
      }

      const sign_up_user_role = localStorage.getItem('sign_up_user_role');
      if (sign_up_user_role) {
        this.user.role = sign_up_user_role;
        localStorage.removeItem('sign_up_user_role');
      }
    },
  };
</script>

<style scoped lang="stylus">
  .line
    width: 100%
    text-align: center
    border-bottom: 1px solid rgba(156,163,175,1)
    line-height: 0em
    margin: 10px 0 10px
    span
      background:#fff
      padding:0 10px
      font-size: 12px
  .sign-up-box
    width: 500px
    @media (max-width: 639px)
      width: 100vw
  .recaptcha-container
    min-height: 425px
  .error-message-container
    min-height: 21px
    max-height: 21px
    .error-message
      font-size: 12px
      color: red
  .password-text
    font-size: 10px
</style>
<style lang="scss" scoped>
  input[type='checkbox'] {
    width: 1.08rem !important;
    height: 1.08rem !important;
    -webkit-appearance: none;
    -moz-appearance: none;
    -o-appearance: none;
    appearance: none;
    box-shadow: none;
    font-size: 0.8rem;
    text-align: center;
    line-height: 1rem;
    border-radius: 0.25rem;
    border: solid 0.7px rgba(235, 45, 124, var(--tw-bg-opacity));
    --tw-bg-opacity: 5;
    background-color: rgba(243, 244, 246, var(--tw-bg-opacity));
  }
  input[type='checkbox']:checked {
    --tw-bg-opacity: 1;
    background-color: rgba(235, 45, 124, var(--tw-bg-opacity));
  }
  input[type='checkbox']:checked::after {
    font-family: 'Font Awesome 5 Free';
    content: '\f00c';
    font-weight: 900;
    color: white;
    line-height: 1rem;
  }
  .Password {
    max-width: 100% !important;
    .Password__field {
      padding: 8px 16px;
      background-color: #f5f8fa !important;
      color: rgba(113, 113, 122, 1);
      height: 36px;
      font-size: 12px;
      border: 0;
      border-radius: 4px;
      outline: 2px solid transparent;
      outline-offset: 2px;
    }
    .Password__strength-meter {
      margin: 5px 0 2px;
    }
  }
</style>



// WEBPACK FOOTER //
// signup_new.vue?064742d5